import React, { FunctionComponent } from 'react';
import { Row } from '@tanstack/react-table';
import { MoreHorizontal, ClipboardCheck, ClipboardPen, QrCode, Link, ExternalLink, Smartphone } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@companion-professional/components';
import { copyTextToClipboard } from '../../lib/copy';
import { getNewSurveyPath } from '../../lib/surveyPath';
import { useClinicSurveysForCheckups } from '../../hooks/useClinicSurveysForCheckups';
import { useNavigate } from 'react-router-dom';
import { CheckupRequestStatusResponse } from '@companion-professional/dctypes';
import { useCheckupUrl } from '../../hooks/useCheckupUrl';

interface CheckupActionRowMenuProps {
  // row is the row that the menu is being shown for.
  row: Row<CheckupRequestStatusResponse>;

  // openQRCodeDialog is a function that opens a dialog to show the QR code for the checkup.
  openQRCodeDialog: (checkup: CheckupRequestStatusResponse) => void;

  // onOpen is a function that is called when a user clicks on a menu item.  It should navigate the user to the
  // appropriate checkup page in vportal.  inNewTab is a boolean that determines if the checkup should be opened in a
  // new tab.
  onOpen: (checkup: CheckupRequestStatusResponse, inNewTab?: boolean) => void;
}

// CheckupActionRowMenu is a dropdown menu that appears on each row of the CheckupTable.  It shows up as a "..." button
// at the end of each row.  It has the following options:
// - Copy Checkup URL
// - Show QR Code Checkup Link
// - Add Health Data (open a survey for that checkup)
export const CheckupActionRowMenu: FunctionComponent<CheckupActionRowMenuProps> = ({
  row,
  openQRCodeDialog,
  onOpen
}) => {
  // Loading surveys here to check if there are surveys assigned to the clinic.  If there are, then we can show the
  // "Add Health Data" option.
  const { surveys } = useClinicSurveysForCheckups();
  const navigate = useNavigate();
  const { getCheckupUrl, error, isPending } = useCheckupUrl();

  return (
    <div className="flex justify-end space-x-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">More</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Vision Portal</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => {
              onOpen(row.original, true);
            }}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            <span>Open in New Tab</span>
          </DropdownMenuItem>
          {!isPending && !error ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Checkup App</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => copyTextToClipboard(getCheckupUrl(row.original.id))}>
                <Link className="mr-2 h-4 w-4" />
                <span>Copy Checkup URL</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => {
                  openQRCodeDialog(row.original);
                }}
              >
                <QrCode className="mr-2 h-4 w-4" />
                <span>Show QR Code Checkup Link</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => {
                  window.open(getCheckupUrl(row.original.id), '_blank');
                }}
              >
                <Smartphone className="mr-2 h-4 w-4" />
                <span>Open Checkup in New Tab</span>
              </DropdownMenuItem>
            </>
          ) : null}

          {surveys && surveys.length > 0 ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Health Surveys</DropdownMenuLabel>
              {surveys.map((survey) => {
                const answeredSurvey = row.original?.answered_surveys?.find((s) => s.survey_id === survey.survey_id);

                const onClick = () => {
                  if (answeredSurvey) {
                    navigate(`/pets/${row.original.pet_id}/answers/${answeredSurvey.answers_id}`);
                  } else {
                    navigate(getNewSurveyPath(row.original.pet_id, survey.survey_id, [row.original.id]));
                  }
                };

                return (
                  <DropdownMenuItem key={survey.survey_id} onClick={onClick}>
                    {answeredSurvey ? (
                      <ClipboardPen className="mr-2 h-4 w-4" />
                    ) : (
                      <ClipboardCheck className="mr-2 h-4 w-4" />
                    )}
                    <span>
                      {answeredSurvey ? 'Edit' : 'Add'} {survey.title || survey.name}
                    </span>
                  </DropdownMenuItem>
                );
              })}
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
